/** The Data quality tab of Scope 1 and Scope 2: per site, how good the figures behind the total are.
 *
 * Counts of rows, never tonnes: how many figures sit at each grade, how many wait for a person to
 * accept them, where the evidence chain breaks, and how many bills the census has not seen. **The
 * counts are the server's, over every figure of the scope** (`GET /inventory/quality-by-site`): this
 * tab used to count the figures the browser had loaded and refused to show anything past ten thousand.
 * The read is the whole scope however large it is, so there is no partial state to describe.
 */

import { Flame, Lightning } from "@phosphor-icons/react";

import { useQualityBySite } from "../../api/queries";
import type { QualitySite } from "../../api/queries";
import { useCoverage } from "../../api/sources";
import { EngagementWindowNote } from "../../components/EngagementWindowNote";
import { ReadFailed } from "../../components/Loaded";
import {
  EmptyState,
  FilterLine,
  EMPTY_FILTERS,
  GridTable,
  InfoPopover,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../../components/ui";
import type { FilterState, GridColumn } from "../../components/ui";
import { quantity } from "../../lib/format";
import { useState } from "react";
import { scopeOfCategory } from "./model";
import { DASH } from "./context";
import type { ScopeContext } from "./context";

interface SiteRow {
  id: string;
  site: string;
  figures: number;
  a: number;
  b: number;
  c: number;
  d: number;
  awaiting: number;
  gaps: number;
  missing: number;
}

function rowsOf(sites: readonly QualitySite[], missingBySite: ReadonlyMap<string, number>): SiteRow[] {
  return sites
    .map((site) => ({
      id: site.facility_id,
      site: site.facility_name,
      figures: site.figures,
      a: site.grade_a,
      b: site.grade_b,
      c: site.grade_c,
      d: site.grade_d,
      awaiting: site.awaiting,
      gaps: site.evidence_gaps,
      missing: missingBySite.get(site.facility_id) ?? 0,
    }))
    .sort((x, y) => x.site.localeCompare(y.site));
}

export function QualityView({ ctx }: { ctx: ScopeContext }) {
  const { scope, period, missing } = ctx;
  const quality = useQualityBySite(period, "location", scope);
  const coverage = useCoverage(period.period_start, period.period_end);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const missingBySite = new Map<string, number>();
  for (const source of coverage.data?.sources ?? []) {
    if (scopeOfCategory(source.category) !== scope) continue;
    missingBySite.set(source.facility_id, (missingBySite.get(source.facility_id) ?? 0) + source.missing);
  }

  const loaded = quality.data;
  const all = loaded ? rowsOf(loaded.sites, missingBySite) : [];
  const needle = filters.search.trim().toLowerCase();
  const rows = needle ? all.filter((row) => row.site.toLowerCase().includes(needle)) : all;
  const dash = loaded === undefined;

  const columns: GridColumn<SiteRow>[] = [
    { id: "site", header: "Site", cell: (row) => row.site },
    { id: "figures", header: "Figures", numeric: true, cell: (row) => quantity(String(row.figures)) },
    { id: "a", header: "Grade A", numeric: true, cell: (row) => String(row.a) },
    { id: "b", header: "Grade B", numeric: true, cell: (row) => String(row.b) },
    { id: "c", header: "Grade C", numeric: true, cell: (row) => String(row.c) },
    { id: "d", header: "Grade D", numeric: true, cell: (row) => String(row.d) },
    { id: "awaiting", header: "Awaiting accept", numeric: true, cell: (row) => String(row.awaiting) },
    { id: "gaps", header: "Evidence gaps", numeric: true, cell: (row) => String(row.gaps) },
    { id: "missing", header: "Missing bills", numeric: true, cell: (row) => String(row.missing) },
    {
      id: "state",
      header: "State",
      cell: (row) =>
        row.missing > 0 || row.gaps > 0 ? (
          <StatusChip tone="crit" size="sm">
            Gaps
          </StatusChip>
        ) : row.b + row.c + row.d > 0 ? (
          <StatusChip tone="warn" size="sm">
            Some estimates
          </StatusChip>
        ) : (
          <StatusChip tone="ok" size="sm">
            Good
          </StatusChip>
        ),
    },
  ];

  return (
    <TablePage
      icon={scope === "1" ? Flame : Lightning}
      title={`Scope ${scope}`}
      extra={<EngagementWindowNote under={ctx.engagement} />}
      tiles={
        <TileStrip label={`Scope ${scope} data quality`}>
          <Tile label="Figures" value={dash ? DASH : quantity(String(loaded.figures))} loading={quality.isPending} />
          <Tile
            label="Grade A figures"
            value={dash ? DASH : `${quantity(String(loaded.grade_a))} of ${quantity(String(loaded.figures))}`}
            infoKey="scope1.grades"
            loading={quality.isPending}
          />
          <Tile
            label="Estimates"
            value={dash ? DASH : quantity(String(loaded.figures - loaded.grade_a))}
            loading={quality.isPending}
          />
          <Tile
            label="Missing bills"
            value={missing === undefined ? DASH : String(missing)}
            infoKey="scope1.missing"
            loading={coverage.isPending}
          />
        </TileStrip>
      }
      state={
        quality.error ? (
          <ReadFailed
            of={`The Scope ${scope} data quality`}
            error={quality.error}
            onRetry={() => void quality.refetch()}
          />
        ) : coverage.error ? (
          <ReadFailed
            of="The expected bills"
            error={coverage.error}
            onRetry={() => void coverage.refetch()}
          />
        ) : undefined
      }
      filters={
        <FilterLine
          filters={[]}
          state={filters}
          onChange={setFilters}
          searchLabel="Search this table"
          actions={<InfoPopover infoKey="scope1.grades" />}
        />
      }
      table={
        <GridTable
          caption={`Scope ${scope} data quality by site`}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowLabel={(row) => row.site}
          loading={quality.isPending}
          empty={<EmptyState title="Nothing to grade yet." compact />}
          testId="scope-quality"
        />
      }
    />
  );
}
